/* Button 038 (Osmo) — staggered per-character text animation.

   In the Astro source the bg/inner/text structure is authored in markup
   (ArbButton.astro) and JS only splits the label into per-character spans.
   Theme buttons are flat inline markup spread across many sections, so this
   enhancer also BUILDS that structure before splitting — producing the same DOM
   the Astro component emits:

     <a class="arb-button button-038 …" data-button-038>
       <span class="button-038__bg"></span>
       <span class="button-038__inner">
         …leading icons…
         <span class="button-038__text" data-button-038-text>
           <span class="button-038__word"><span class="button-038__char" style="--index:0">L</span>…</span>
         </span>
         …trailing icons…
       </span>
     </a>

   Until the script runs, the .arb-button:not([data-button-038]) flat styles
   apply, so the button renders correctly without JS (it just won't animate).
   Opt a button out permanently with [data-button-038-skip] — used on functional
   product-form buttons whose labels are swapped dynamically (Add to Cart /
   Sold Out), where splitting the text would interfere. */
(function () {
  function enhance(button) {
    button.setAttribute('data-button-038-ready', 'true');

    var textElement = button.querySelector('[data-button-038-text]');

    // Build the inner structure from the button's existing children unless it
    // was already authored with one (forward-compatible with snippet markup).
    if (!textElement) {
      var inner = document.createElement('span');
      inner.className = 'button-038__inner';

      textElement = document.createElement('span');
      textElement.className = 'button-038__text';
      textElement.setAttribute('data-button-038-text', '');

      var label = '';
      var textInserted = false;

      // Snapshot child nodes — appendChild moves them out as we iterate.
      Array.prototype.forEach.call(Array.prototype.slice.call(button.childNodes), function (node) {
        if (node.nodeType === Node.TEXT_NODE) {
          label += node.textContent;
          // Insert the text span at the position of the first text run so
          // leading/trailing icons keep their source order.
          if (!textInserted) {
            inner.appendChild(textElement);
            textInserted = true;
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          inner.appendChild(node);
        }
      });

      if (!textInserted) {
        inner.appendChild(textElement);
      }

      textElement.textContent = label.replace(/\s+/g, ' ').trim();

      var bg = document.createElement('span');
      bg.className = 'button-038__bg';

      button.textContent = '';
      button.appendChild(bg);
      button.appendChild(inner);
    }

    button.classList.add('button-038');
    button.setAttribute('data-button-038', '');

    // Split the label into per-character spans carrying their stagger --index,
    // grouped into per-word wrappers.
    if (textElement.dataset.button038TextReady !== 'true') {
      var text = textElement.textContent;
      textElement.textContent = '';
      textElement.dataset.button038TextReady = 'true';

      /* ARB-333: the characters are grouped into per-WORD wrappers, and the real
         spaces between them are appended as plain text nodes — the only soft-wrap
         opportunities the label has left.

         WHY, because a flat per-character split looks harmless: a character span
         has to be `display: inline-block` (`translate` does not apply to a
         non-atomic inline, and the hover slide is a translate), and for
         line-breaking purposes an atomic inline is treated as U+FFFC, whose
         Unicode break class allows a break on EITHER side of it. So every
         character became a break opportunity, and the reported mobile hero button
         wrapped as "SHOP OUTDOOR / GROUND" + "S CARE" — the word GROUNDS split
         down the middle. `white-space: nowrap` on the word wrapper confines the
         breaks to the spaces.

         The spaces are plain text nodes rather than spans, so a break at one
         collapses it the way a normal space does; a `white-space: pre` span at a
         line start would have printed as a leading indent. --index keeps counting
         across words, so the stagger is unchanged apart from no longer spending
         an index on a glyphless space. */
      var index = 0;
      var word = null;

      Array.prototype.forEach.call(Array.from(text), function (char) {
        if (char === ' ') {
          word = null;
          textElement.appendChild(document.createTextNode(char));
          return;
        }

        if (!word) {
          word = document.createElement('span');
          word.className = 'button-038__word';
          textElement.appendChild(word);
        }

        var span = document.createElement('span');
        span.className = 'button-038__char';
        span.textContent = char;
        span.style.setProperty('--index', index++);
        word.appendChild(span);
      });
    }
  }

  // ARB-359: the per-character hover animation (arb-tokens.css) slides every
  // character up by a fixed one-line-height amount to reveal a text-shadow
  // duplicate — it only reads correctly when the label is a single line. If
  // the label wraps, line 2's characters sit one line below line 1 and get
  // the same upward translate, sliding straight into line 1 instead of
  // revealing their own duplicate. Detect wrapped labels (by comparing each
  // character span's offsetTop) and mark them so CSS can fall back to a
  // plain background/color swap instead of the per-character transform.
  function updateWrapState(button) {
    var textElement = button.querySelector('[data-button-038-text]');
    if (!textElement) return;
    /* The CHARACTER spans, not textElement.children — since ARB-333 the direct
       children are per-word wrappers, and it is a character landing on a second
       line that this is looking for either way. */
    var spans = textElement.querySelectorAll('.button-038__char');
    if (!spans.length) return;
    var firstTop = spans[0].offsetTop;
    var wraps = false;
    for (var i = 1; i < spans.length; i++) {
      if (spans[i].offsetTop !== firstTop) {
        wraps = true;
        break;
      }
    }
    button.classList.toggle('button-038--wraps', wraps);
  }

  function updateAllWrapStates() {
    var buttons = document.querySelectorAll('[data-button-038]');
    Array.prototype.forEach.call(buttons, updateWrapState);
  }

  function initButton038() {
    var buttons = document.querySelectorAll(
      '.arb-button:not([data-button-038-ready]):not([data-button-038-skip])'
    );
    Array.prototype.forEach.call(buttons, function (button) {
      enhance(button);
      updateWrapState(button);
    });
  }

  window.arbInitButton038 = initButton038;

  document.addEventListener('DOMContentLoaded', initButton038);
  /* Theme editor re-renders sections without a full reload. */
  document.addEventListener('shopify:section:load', initButton038);

  // Wrapping can change across breakpoints/rotation; re-measure on resize.
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(updateAllWrapStates, 150);
  });
})();
